import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useMutation, useAction } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import type { Id } from '../../../convex/_generated/dataModel';

// Helper function to normalize email
const normalizeEmail = (email: string): string => {
  return email.trim().toLowerCase();
};

const createUserSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Please enter a valid email address').transform(normalizeEmail),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

const editUserSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Please enter a valid email address').transform(normalizeEmail),
});

type CreateUserFormData = z.infer<typeof createUserSchema>;
type EditUserFormData = z.infer<typeof editUserSchema>;

interface User {
  id: Id<"users">;
  name: string;
  email: string;
  isAdmin?: boolean;
  createdAt: number;
}

interface UserManagementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  user?: User;
  onSuccess?: () => void;
}

export function UserManagementDialog({
  open,
  onOpenChange,
  mode,
  user,
  onSuccess,
}: UserManagementDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const createUser = useAction(api.users.adminCreateUserWithPassword);
  const updateUser = useMutation(api.users.adminUpdateUser);

  const isEdit = mode === 'edit';
  const schema = isEdit ? editUserSchema : createUserSchema;

  const form = useForm<CreateUserFormData | EditUserFormData>({
    resolver: zodResolver(schema),
    defaultValues: isEdit && user
      ? {
          name: user.name,
          email: user.email,
        }
      : {
          name: '',
          email: '',
          password: '',
        },
  });

  // Reset form when user or mode changes
  useEffect(() => {
    if (isEdit && user) {
      form.reset({
        name: user.name,
        email: user.email,
      });
    } else {
      form.reset({
        name: '',
        email: '',
        password: '',
      });
    }
  }, [user, mode, form, isEdit]);

  const handleSubmit = async (data: CreateUserFormData | EditUserFormData) => {
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    
    try {
      if (isEdit && user) {
        await updateUser({
          userId: user.id,
          name: data.name,
          email: data.email, // Already normalized by zod transform
        });
        toast.success('User updated successfully');
      } else {
        const createData = data as CreateUserFormData;
        await createUser({
          name: createData.name,
          email: createData.email, // Already normalized by zod transform
          password: createData.password,
        });
        toast.success('User created successfully');
      }
      
      form.reset();
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && !isSubmitting) {
      form.reset();
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? 'Edit User' : 'Create New User'}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Update the user information below.'
              : 'Create a new user account with email and password.'
            }
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter full name"
                      disabled={isSubmitting}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="Enter email address"
                      disabled={isSubmitting}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {!isEdit && (
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder="Enter temporary password"
                        disabled={isSubmitting}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}


            <div className="flex justify-end space-x-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting
                  ? (isEdit ? 'Updating...' : 'Creating...')
                  : (isEdit ? 'Update User' : 'Create User')
                }
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}